import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { ConnectionCardProps } from "./ConnectionCard";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  connectionContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#fff7ed",
    borderRadius: 8,
  },
  connectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#ea580c",
    marginBottom: 12,
  },
  twoColumnLayout: {
    flexDirection: "row",
    gap: 12,
  },
  column: {
    flex: 1,
  },
  contextTitle: {
    color: "#2563eb",
  },
  applicationTitle: {
    color: "#059669",
  },
  professionsTitle: {
    color: "#9333ea",
  },
  professionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginBottom: 12,
  },
  professionTag: {
    backgroundColor: "#f3f4f6",
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    color: "#374151",
  },
  professionDetailsTitle: {
    color: "#4f46e5",
  },
  professionDetailCard: {
    backgroundColor: "#f8fafc",
    padding: 8,
    borderRadius: 4,
    marginBottom: 6,
  },
  professionDetailName: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#4b5563",
    marginBottom: 2,
  },
  professionDetailValue: {
    fontSize: 9,
    color: "#374151",
  },
  activitiesTitle: {
    color: "#d97706",
  },
  activityItem: {
    flexDirection: "row",
    marginBottom: 4,
  },
  activityBullet: {
    width: 10,
    fontSize: 9,
    marginRight: 4,
    color: "#d97706",
  },
  activityText: {
    fontSize: 9,
    color: "#374151",
    flex: 1,
  },
  skillDevelopmentTitle: {
    color: "#0891b2",
  },
  skillTag: {
    backgroundColor: "#e0f2fe",
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    color: "#0e7490",
  },
  // Additional sections
  additionalSectionContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f8fafc",
    borderRadius: 8,
  },
  usageContextTitle: {
    color: "#4f46e5",
  },
  implementationGuideTitle: {
    color: "#0d9488",
  },
  skillFocusTitle: {
    color: "#d97706",
  },
  skillFocusContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 8,
  },
});

const ConnectionDocument = ({
  courseName,
  prompt,
  gradeLevel,
  connections,
  imageUrl,
  customizationSettings,
  author,
  createdAt,
  usageContext,
  implementationGuide,
  skillFocus,
}: ConnectionCardProps) => {
  const { t } = useTranslation();

  const sectionMarkdownStyle = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };

  return (
    <PdfLayout
      courseName={courseName}
      gradeLevel={gradeLevel}
      topic={prompt}
      imageUrl={imageUrl}
      headerTextColor="#ea580c"
      customizationSettings={customizationSettings}
      author={author}
      createdDate={createdAt}
    >
      {/* Usage Context Section */}
      {usageContext && (
        <View wrap={false} style={styles.additionalSectionContainer}>
          <Text
            style={[
              commonStyles.sectionTitle,
              styles.usageContextTitle,
              {
                fontSize: scaleFontSize(
                  commonStyles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t("connections.usageContext")}
          </Text>
          <MarkdownRenderer
            customStyle={sectionMarkdownStyle}
            content={usageContext}
          />
        </View>
      )}

      {/* Implementation Guide Section */}
      {implementationGuide && (
        <View wrap={false} style={styles.additionalSectionContainer}>
          <Text
            style={[
              commonStyles.sectionTitle,
              styles.implementationGuideTitle,
              {
                fontSize: scaleFontSize(
                  commonStyles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t("connections.implementationGuide")}
          </Text>
          <MarkdownRenderer
            customStyle={sectionMarkdownStyle}
            content={implementationGuide}
          />
        </View>
      )}

      {/* Skill Focus Section */}
      {skillFocus && skillFocus.length > 0 && (
        <View wrap={false} style={styles.additionalSectionContainer}>
          <Text
            style={[
              commonStyles.sectionTitle,
              styles.skillFocusTitle,
              {
                fontSize: scaleFontSize(
                  commonStyles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t("connections.skillFocus")}
          </Text>
          <View style={styles.skillFocusContainer}>
            {skillFocus.map((skill, idx) => (
              <MarkdownRenderer
                key={idx}
                customStyle={{
                  text: {
                    text: {
                      ...styles.professionTag,
                      fontSize: scaleFontSize(
                        styles.professionTag.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      ...styles.professionTag,
                    },
                  },
                }}
                content={skill}
              />
            ))}
          </View>
        </View>
      )}

      {/* Connections */}
      {connections?.map((connection, index) => (
        <View wrap={false} key={index} style={styles.connectionContainer}>
          <MarkdownRenderer
            customStyle={{
              text: {
                text: {
                  ...styles.connectionTitle,
                  fontSize: scaleFontSize(
                    styles.connectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              },
            }}
            content={connection.title}
          />

          {/* First row - Context & Professions */}
          <View style={styles.twoColumnLayout}>
            <View style={styles.column}>
              {/* Context Description */}
              <View>
                <Text
                  style={[
                    commonStyles.sectionTitle,
                    styles.contextTitle,
                    {
                      fontSize: scaleFontSize(
                        commonStyles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("connections.context")}
                </Text>
                <MarkdownRenderer
                  customStyle={sectionMarkdownStyle}
                  content={connection.context_description}
                />
              </View>

              {/* Real World Application */}
              {connection.real_world_application && (
                <View style={{ marginTop: 12 }}>
                  <Text
                    style={[
                      commonStyles.sectionTitle,
                      styles.applicationTitle,
                      {
                        fontSize: scaleFontSize(
                          commonStyles.sectionTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("connections.connectionsApplication")}
                  </Text>
                  <MarkdownRenderer
                    content={connection.real_world_application}
                    customStyle={sectionMarkdownStyle}
                  />
                </View>
              )}
            </View>

            <View style={styles.column}>
              {/* Professions */}
              <Text
                style={[
                  commonStyles.sectionTitle,
                  styles.professionsTitle,
                  {
                    fontSize: scaleFontSize(
                      commonStyles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("connections.professions")}
              </Text>
              <View style={styles.professionsContainer}>
                {connection.professions.map((profession, idx) => (
                  <MarkdownRenderer
                    key={idx}
                    customStyle={{
                      text: {
                        text: {
                          ...styles.professionTag,
                          fontSize: scaleFontSize(
                            styles.professionTag.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: {
                          ...styles.professionTag,
                        },
                      },
                    }}
                    content={profession}
                  />
                ))}
              </View>

              {/* Profession Details */}
              {connection.profession_details &&
                Object.keys(connection.profession_details).length > 0 && (
                  <View>
                    <Text
                      style={[
                        commonStyles.sectionTitle,
                        styles.professionDetailsTitle,
                        {
                          fontSize: scaleFontSize(
                            commonStyles.sectionTitle.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      ]}
                    >
                      {t("connections.professionDetails")}
                    </Text>
                    {Object.entries(connection.profession_details).map(
                      ([key, value], idx) => (
                        <View key={idx} style={styles.professionDetailCard}>
                          <Text
                            style={[
                              styles.professionDetailName,
                              {
                                fontSize: scaleFontSize(
                                  styles.professionDetailName.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                              },
                            ]}
                          >
                            {key}
                          </Text>
                          <MarkdownRenderer
                            customStyle={{
                              text: {
                                text: {
                                  ...styles.professionDetailValue,
                                  fontSize: scaleFontSize(
                                    styles.professionDetailValue.fontSize,
                                    customizationSettings?.fontSize,
                                  ),
                                },
                              },
                            }}
                            content={value}
                          />
                        </View>
                      ),
                    )}
                  </View>
                )}
            </View>
          </View>

          {/* Second row - Classroom Activities & Skill Development */}
          {((connection.classroom_activities ?? []).length > 0 ||
            (connection.skill_development ?? []).length > 0) && (
            <View style={[styles.twoColumnLayout, { marginTop: 12 }]}>
              {/* Classroom Activities */}
              {connection.classroom_activities &&
                connection.classroom_activities.length > 0 && (
                  <View style={styles.column}>
                    <Text
                      style={[
                        commonStyles.sectionTitle,
                        styles.activitiesTitle,
                        {
                          fontSize: scaleFontSize(
                            commonStyles.sectionTitle.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      ]}
                    >
                      {t("connections.classroomActivities")}
                    </Text>
                    {connection.classroom_activities.map((activity, idx) => (
                      <View key={idx} style={styles.activityItem}>
                        <Text style={styles.activityBullet}>•</Text>
                        <MarkdownRenderer
                          customStyle={{
                            text: {
                              text: {
                                ...styles.activityText,
                                fontSize: scaleFontSize(
                                  styles.activityText.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                              },
                            },
                          }}
                          content={activity}
                        />
                      </View>
                    ))}
                  </View>
                )}

              {/* Skill Development */}
              {connection.skill_development &&
                connection.skill_development.length > 0 && (
                  <View style={styles.column}>
                    <Text
                      style={[
                        commonStyles.sectionTitle,
                        styles.skillDevelopmentTitle,
                        {
                          fontSize: scaleFontSize(
                            commonStyles.sectionTitle.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      ]}
                    >
                      {t("connections.skillDevelopment")}
                    </Text>
                    <View style={styles.professionsContainer}>
                      {connection.skill_development.map((skill, idx) => (
                        <MarkdownRenderer
                          key={idx}
                          customStyle={{
                            text: {
                              text: {
                                ...styles.skillTag,
                                fontSize: scaleFontSize(
                                  styles.skillTag.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                              },
                              container: {
                                ...styles.skillTag,
                              },
                            },
                          }}
                          content={skill}
                        />
                      ))}
                    </View>
                  </View>
                )}
            </View>
          )}
        </View>
      ))}
    </PdfLayout>
  );
};

function ConnectionCardPDF(props: ConnectionCardProps) {
  return <ConnectionDocument {...props} />;
}

export default ConnectionCardPDF;
